import React, { Fragment, useMemo, useState } from "react";
import useNotiStack from "../../hooks/useNotiStack";
import { DECLINE, EDIT, EMAIL_VERIFIED, STATUS_COLORS, USER_STATUS } from "../../enums/statusEnums";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import createUsersQueryOptions from "../../queryOptions/createUsersQueryOptions";
import { approveUser } from "../../config/apisauce";
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import { GenericAvatar } from "../generic/GenericAvatar";
import { DECLINE_CHECKLIST, GENERIC_COLUMN_PROPS } from "../../enums/generalEnums";
import { formatDate } from "../../helpers/dateHelper";
import useDebounce from "react-use/esm/useDebounce";
import { MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { PAGE_OPTIONS } from "../../enums/dataGridEnums";
import { ProfileDetails } from "../../pages/admin/adminHomePage/profileDetails/ProfileDetails";
import { ROLES } from "../../enums/RoleEnum";
import CustomDataGrid, { CustomNoResultsOverlay } from "./CustomDataGrid";
import { GenericCircleIcon } from "../generic/GenericCircleIcon";
import { GenericDialog } from "../generic/GenericDialog";
import { ReasonDialog } from "../../pages/admin/dialogs/ReasonDialog";
import { LoadingButton } from "@mui/lab";
import { snakeToSentence } from "../../helpers/stringHelper";
import { DAY_ONLY_FORMAT } from "../../enums/dateEnums";
import { router } from "@inertiajs/react";

export const ApprovalProfileTable = ({ minified = false, bypassOndato = false }) => {
  const { success } = useNotiStack();
  const [openReasonDialog, setOpenReasonDialog] = useState(false);
  const [openProfileDetailsDialog, setOpenProfileDetailsDialog] = useState(false);
  const [dialogDetails, setDialogDetails] = useState({});
  const [details, setDetails] = useState("");
  const [stillProcessing, setStillProcessing] = useState([]);
  const [userId, setUserId] = useState(null);
  const [acceptConfirmationState, setAcceptConfirmationState] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const handleOpenProfileDetailsDialog = (id) => {
    setUserId(id);
    setOpenProfileDetailsDialog(true);
  };

  const handleCloseProfileDetailsDialog = () => {
    setUserId(null);
    setOpenProfileDetailsDialog(false);
  };

  const handleOpenDialog = (label, data, checklists, action) => {
    setDialogDetails({ label: label, data: data, checklists: checklists, action: action });
    setOpenReasonDialog(true);
    setOpenProfileDetailsDialog(false);
  };

  const handleCloseReasonDialog = () => {
    setOpenReasonDialog(false);
    setDialogDetails({});
    refetch();
  };

  const handleAcceptUser = async (values) => {
    setStillProcessing((prev) => [...prev, values?.id]);
    const params = {
      user_id: values?.id,
      email: values?.email,
      name: values?.name,
    };
    const { ok } = await approveUser(params);
    if (ok) {
      success("User has been accepted!");
      refetch();
    }
    setAcceptConfirmationState(false);
    setStillProcessing((prev) => prev?.filter((item) => item !== values?.id));
  };

  const handleAcceptChecker = (values) => {
    if (bypassOndato) {
      setAcceptConfirmationState(true);
      setSelectedCreator(values);
    } else {
      handleAcceptUser(values);
    }
  };

  const [keyword, setKeyword] = useState("");
  const [tableState, setTableState] = useState({
    filter: {
      search: "",
      status: [USER_STATUS.PENDING],
      "roles.name": [ROLES.CREATOR_ROLE, ROLES.PENDING_CREATOR_ROLE],
    },
    sort: ["-created_at"],
    include: ["details", "ondatoDetails", "details.profilePicture"],
    pageSize: minified ? 5 : 10,
    page: 1,
  });

  const {
    data: users,
    isFetching,
    refetch,
  } = useQuery(createUsersQueryOptions(tableState, { placeholderData: keepPreviousData }));

  const columns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      flex: 1.2,
      renderCell: ({ row }) => {
        return (
          <Stack direction={"row"} alignItems={"center"} gap={1.5} mt={0.5}>
            <GenericAvatar
              location={row?.details?.profile_picture?.location}
              avatarId={row?.id}
              size={"42px"}
              backgroundColor={"background.default"}
            />
            <Typography fontWeight={700}>{row?.name}</Typography>
          </Stack>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "profession",
      headerName: "Profession",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => row?.details?.profession,
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "created_at",
      headerName: "Request Date",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "referral_code",
      headerName: "Referral Code",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) =>
        row?.details?.meta?.reffered_by ?? (
          <Typography mt={1.7} color="grey.light">
            N/A
          </Typography>
        ),
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: bypassOndato ? 1 : 0.7,
      renderCell: ({ row }) => {
        return (
          <Typography
            mt={1.7}
            fontWeight={700}
            textTransform={"capitalize"}
            color={bypassOndato ? "error" : STATUS_COLORS[row?.status]}
          >
            {row?.status === EMAIL_VERIFIED ? "Ondato Unverified" : snakeToSentence(row?.status)}
          </Typography>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
    ...(!bypassOndato
      ? [
          {
            field: "request-edit",
            headerName: "Request Edit",
            minWidth: 140,
            flex: 1.4,
            renderCell: ({ row }) => {
              const processing = stillProcessing?.includes(row?.id);
              return (
                <Button
                  fullWidth
                  disabled={processing}
                  variant="outlined"
                  onClick={() => handleOpenDialog("Edit", row, [], EDIT)}
                >
                  Request Edit
                </Button>
              );
            },
            ...GENERIC_COLUMN_PROPS,
          },
        ]
      : []),
    {
      field: "details",
      headerName: "Details",
      minWidth: 280,
      flex: bypassOndato ? 1 : 2.5,
      renderCell: ({ row }) => {
        return (
          <Stack gap={1} direction={"row"} mt={1.1}>
            <Button
              fullWidth
              variant="contained"
              sx={{ color: "#FFF" }}
              onClick={() => handleOpenProfileDetailsDialog(row?.id)}
            >
              Details
            </Button>
            {!bypassOndato && (
              <Button
                disabled={!row?.ondato_details?.ondato_url}
                fullWidth
                variant="contained"
                onClick={() => window.open(row?.ondato_details?.ondato_url, "_blank")}
              >
                Ondato Details
              </Button>
            )}
          </Stack>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 280,
      flex: bypassOndato ? 1.6 : 2.5,
      renderCell: ({ row }) => {
        const processing = stillProcessing?.includes(row?.id);
        return (
          <Stack gap={1} direction={"row"} mt={1.1}>
            <Button
              disabled={processing}
              onClick={() => handleOpenDialog("Declining", row, DECLINE_CHECKLIST, DECLINE)}
              fullWidth
              variant="contained"
              color="error"
              sx={{ color: "#FFF" }}
            >
              Decline
            </Button>
            <LoadingButton
              fullWidth
              onClick={() => handleAcceptChecker(row)}
              size="small"
              loading={processing}
              variant="contained"
              disabled={processing}
              color={bypassOndato ? "warning" : "success"}
            >
              Accept
            </LoadingButton>
          </Stack>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
  ];

  const [,] = useDebounce(
    () => {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          search: keyword,
        },
        page: 1,
      }));
    },
    1000,
    [keyword]
  );

  return (
    <Fragment>
      <Card sx={{ boxShadow: "none" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xxs: "column", mds: "row" },
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary">
              {bypassOndato ? "Ondato Unverified & Rejected Accounts" : "Incoming Profile Approval"}
            </Typography>
            <Typography fontWeight={500}>
              {bypassOndato
                ? "These accounts have completed all required steps but remain unverified or have been rejected by Ondato."
                : "You have new requests for profile approval. Review the details and decide who you’d like to work with."}
            </Typography>
          </Box>
          <Box width={{ xxs: "100%", mds: minified ? "130px" : "350px" }}>
            {minified && (
              <Grid container item justifyContent={"flex-end"} size={{ xs: 12, sm: 6 }}>
                <Button
                  variant="outlined"
                  sx={{ width: { xs: "100%", sm: "102px" } }}
                  onClick={() => router.visit("/admin-collab-creators")}
                >
                  See All
                </Button>
              </Grid>
            )}
            {!minified && (
              <FormControl fullWidth>
                <OutlinedInput
                  fullWidth
                  variant="outlined"
                  placeholder="Search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <MagnifyingGlass />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            )}
          </Box>
        </Box>

        {/* MOBILE VERSION */}
        <Box sx={{ display: { xxs: "flex", xsm: "none" }, flexDirection: "column", width: "100%" }}>
          <CreatorCardListMobile
            creators={users}
            setTableState={setTableState}
            handleOpenProfileDetailsDialog={handleOpenProfileDetailsDialog}
            handleOpenDialog={handleOpenDialog}
            handleAcceptChecker={handleAcceptChecker}
            bypassOndato={bypassOndato}
            minified={minified}
          />
        </Box>

        {/* TABLE */}
        <Box height={minified ? "325px" : "630px"} sx={{ display: { xxs: "none", xsm: "block" }, overflowX: "auto" }}>
          <CustomDataGrid
            columns={columns}
            pageSizeOptions={PAGE_OPTIONS}
            rows={users?.data ?? []}
            rowCount={users?.total ?? 0}
            paginationMode="server"
            paginationModel={{
              pageSize: users?.per_page ?? 10,
              page: typeof users?.current_page === "number" ? users.current_page - 1 : 0,
            }}
            onPaginationModelChange={({ page, pageSize }) => {
              setTableState((prev) => ({
                ...prev,
                page: pageSize !== prev.pageSize ? 1 : page + 1,
                pageSize,
              }));
            }}
            slotProps={{
              pagination: {
                sx: {
                  "& .MuiTablePagination-selectLabel": {
                    display: "block",
                  },
                  "& .MuiTablePagination-input": {
                    display: "inline-flex",
                  },
                },
              },
            }}
            hideFooter={minified}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            checkboxSelection={false}
            loading={!users || isFetching}
          />
        </Box>
      </Card>
      <ReasonDialog
        details={details}
        setDetails={setDetails}
        dialogDetails={dialogDetails}
        openDialog={openReasonDialog}
        handleCloseDialog={() => handleCloseReasonDialog()}
      />
      <ProfileDetails
        bypassOndato={bypassOndato}
        openDialog={openProfileDetailsDialog}
        userId={userId}
        handleClose={handleCloseProfileDetailsDialog}
        handleOpenReasonDialog={handleOpenDialog}
        handleAcceptUser={handleAcceptChecker}
        refetchCreatorList={() => refetch()}
      />
      <GenericDialog
        title={"Bypass Verification"}
        width={"550px"}
        openDialog={acceptConfirmationState}
        handleClose={() => setAcceptConfirmationState(false)}
      >
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} mb={2}>
          <GenericCircleIcon color={"primary.main"}>
            <Warning size={96} weight="thin" />
          </GenericCircleIcon>
        </Box>
        <Stack gap={3} alignItems={"center"}>
          <Typography variant="subtitle1" textAlign={"center"}>
            You are about to accept a user{" "}
            <b>{selectedCreator?.status === EMAIL_VERIFIED ? "without Ondato verification." : "Ondato Rejected."}</b>
            <br></br>
            Are you sure you want to continue?
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} gap={1} mt={2}>
          <Button
            variant="outlined"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            onClick={() => setAcceptConfirmationState(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            onClick={() => {
              handleAcceptUser(selectedCreator);
              setSelectedCreator(null);
              setAcceptConfirmationState(false);
            }}
          >
            Approve Anyway
          </Button>
        </Stack>
      </GenericDialog>
    </Fragment>
  );
};

const CreatorCardListMobile = ({
  creators,
  setTableState,
  handleOpenProfileDetailsDialog,
  handleOpenDialog,
  handleAcceptChecker,
  bypassOndato,
  minified,
}) => {
  const creatorList = useMemo(() => creators?.data || [], [creators]);

  if (!creatorList.length) {
    return (
      <Box py={2} textAlign="center">
        <CustomNoResultsOverlay />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          height: "650px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {creatorList.map((creator, index) => (
          <Box key={`creator-list-card-${index}`}>
            <CreatorCardMobile
              creator={creator}
              handleOpenProfileDetailsDialog={handleOpenProfileDetailsDialog}
              handleOpenDialog={handleOpenDialog}
              handleAcceptChecker={handleAcceptChecker}
              bypassOndato={bypassOndato}
            />
            {index !== creatorList.length - 1 && <Divider sx={{ borderColor: "borders.main" }} />}
          </Box>
        ))}
      </Box>

      {!minified && (
        <Box>
          <TablePagination
            labelRowsPerPage="Per page:"
            component="div"
            count={creators.total}
            page={creators.current_page - 1}
            rowsPerPageOptions={PAGE_OPTIONS}
            onPageChange={(event, newPage) =>
              setTableState((prev) => ({
                ...prev,
                page: newPage + 1,
              }))
            }
            rowsPerPage={creators.per_page}
            onRowsPerPageChange={(event) =>
              setTableState((prev) => ({
                ...prev,
                pageSize: parseInt(event.target.value, 10),
                page: 1,
              }))
            }
          />
        </Box>
      )}
    </Box>
  );
};

const CreatorCardMobile = ({
  creator,
  handleOpenProfileDetailsDialog,
  handleOpenDialog,
  handleAcceptChecker,
  bypassOndato = false,
}) => {
  return (
    <Box sx={{ padding: 2 }}>
      <Grid container>
        <Grid container item size={10} alignItems={"center"}>
          <Grid item size={5}>
            <GenericAvatar
              location={creator?.details?.profile_picture?.location}
              avatarId={creator?.id}
              size={"92px"}
              backgroundColor={"background.default"}
            />
          </Grid>
          <Grid item size={7}>
            <Stack>
              <Typography fontWeight={700} noWrap>
                {creator?.name}
              </Typography>
              <Typography noWrap>{creator?.details?.profession}</Typography>
              <Typography
                fontWeight={700}
                textTransform={"capitalize"}
                color={bypassOndato ? "error" : STATUS_COLORS[creator?.status]}
              >
                {creator?.status === EMAIL_VERIFIED ? "Ondato Unverified" : snakeToSentence(creator?.status)}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        <Grid container item justifyContent={"flex-end"} size={2}>
          <Typography color="text.light">{formatDate(new Date(creator?.created_at), DAY_ONLY_FORMAT)}</Typography>
        </Grid>
      </Grid>
      <Stack direction={"row"} gap={0.5} mt={1.5}>
        <Button
          fullWidth
          variant="contained"
          color="error"
          onClick={() => handleOpenDialog("Declining", creator, DECLINE_CHECKLIST, DECLINE)}
        >
          Decline
        </Button>
        <Button
          fullWidth
          variant="contained"
          color={bypassOndato ? "warning" : "success"}
          onClick={() => handleAcceptChecker(creator)}
        >
          Accept
        </Button>
      </Stack>
      <Stack direction={"column"} mt={1.5} gap={1.5}>
        <Button fullWidth variant="contained" onClick={() => handleOpenProfileDetailsDialog(creator?.id)}>
          Details
        </Button>
        {!bypassOndato && (
          <Fragment>
            <Button
              disabled={!creator?.ondato_details?.ondato_url}
              fullWidth
              variant="contained"
              onClick={() => window.open(creator?.ondato_details?.ondato_url, "_blank")}
            >
              Ondato Details
            </Button>
            <Button fullWidth variant="outlined" onClick={() => handleOpenDialog("Edit", creator, [], EDIT)}>
              Request Edit
            </Button>
          </Fragment>
        )}
      </Stack>
    </Box>
  );
};
